import type VueRouter from "vue-router";
import type { Route } from "vue-router";
import { trackNavigationWait } from "@/util/navigationProgress";

/**
 * Starts loading the lazy page chunks `route` renders. Null when every one is loaded already;
 * otherwise settles once all have, leaving a failed load for the router to report on its retry.
 */
export function prefetchRouteComponents(route: Route): Promise<unknown> | null {
    const loads: Promise<unknown>[] = [];

    for (const record of route.matched) {
        for (const component of Object.values(record.components)) {
            // A loaded component is an options object or a constructor carrying a cid; a lazy
            // one is a bare loader function.
            if (typeof component === "function" && (component as any).cid === undefined) {
                loads.push(Promise.resolve().then(() => (component as () => unknown)()));
            }
        }
    }

    return loads.length ? Promise.allSettled(loads) : null;
}

/**
 * Vue Router 3 loads a route's lazy chunks only after every beforeEach guard has resolved, so a
 * first visit would wait on the auth guard and then on the chunk. Installed ahead of that guard,
 * this runs them together; the router's own later call gets the cached import.
 */
export function installRoutePrefetchGuard(router: VueRouter): void {
    router.beforeEach((to, _from, next) => {
        const loading = prefetchRouteComponents(to);
        if (loading) void trackNavigationWait(loading);
        next();
    });
}
